import type { Fraction } from '../domain/points';
import { uuidv7 } from '../platform/uuid';
import { toRelayDateTime } from './relay';
import type { Store } from './port';
import type { ExamLogRow, LogReferenceType } from './rows';
import { EXAM_LOG_TYPES, logTypeId, type LogTypeName } from './seed';

/**
 * `ExamLog` ist die einzige Ablage des Bewertungsstands — es gibt keine Ergebnistabellen.
 *
 * Die Tabelle wird **nur angehängt**, nie geändert: eine Korrektur ist ein neuer Eintrag, und
 * beim Lesen gilt je Schlüssel der jüngste nach `creationDate`. Daraus folgt zweierlei: der
 * Stand ist jederzeit aus dem Protokoll herstellbar, und ein erneut gesendeter Eintrag ändert
 * am Ergebnis nichts.
 *
 * ⚠️ Maßgeblich ist `creationDate` — wann der Prüfer bewertet hat —, nicht `storeDate`.
 * Damit ist die Geräteuhr ein fachliches Bauteil; der Abgleich mit dem Server gehört zu Phase 4.
 */

export interface LogDraft {
  type: LogTypeName;
  examID: string;
  candidateID: string;
  examinerID: string | null;
  actorID?: string | null;
  referenceType?: LogReferenceType | null;
  referenceIdentifier?: string | null;
  points?: Fraction;
  value?: string;
  /** Nur für Tests und Nachträge; sonst wird der Zeitpunkt hier gesetzt. */
  creationDate?: string;
}

let gerät = { deviceIdentifier: 'unbekannt', deviceName: 'unbekannt' };

/** Einmal beim Start aus `@capacitor/device` setzen — Relay verlangt beide Felder als NOT NULL. */
export function setDeviceInfo(info: { deviceIdentifier: string; deviceName: string }): void {
  gerät = info;
}

const vertragVon = (name: LogTypeName) => EXAM_LOG_TYPES.find((t) => t.name === name)!;

/**
 * Kommentare gehören zur Aufgabe **oder** zur ganzen Station — Relay führt dafür eine Menge
 * (`seed.sql`, seit 2026-09-16; Handschrift auf Wunsch des Nutzers vom 2026-10-14).
 * Ohne das scheiterte jedes Autospeichern mit einem Stationskommentar (writeFailed-Flut).
 */
const AUCH_AN_STATION: ReadonlySet<LogTypeName> = new Set([
  'TEXT_COMMENT', 'AUDIO_COMMENT', 'VIDEO_COMMENT', 'IMAGE_COMMENT', 'HANDWRITING_COMMENT',
]);

export async function appendLog(store: Store, draft: LogDraft): Promise<ExamLogRow> {
  const vertrag = vertragVon(draft.type);
  const ziel = draft.referenceType ?? null;
  const anStation = ziel === 'EXAM_ITEM_CONTAINER' && AUCH_AN_STATION.has(draft.type);
  if (ziel !== vertrag.referenceType && !anStation) {
    // Denselben Vertrag prüft der Server beim Schreiben (Relay `memory/log-tables.md`).
    // Lokal zu scheitern ist billiger als eine Zeile, die beim Übertragen abgewiesen wird.
    throw new Error(
      'Zielebene passt nicht zu ' + draft.type + ': erwartet ' + vertrag.referenceType + ', bekommen ' + ziel,
    );
  }

  // ⚠️ **Leere Kennung heisst `null`, nicht `''`.** `ExamLog.actorID`, `candidateID`,
  // `examID` und `examinerID` verweisen mit Fremdschlüsseln auf ihre Tabellen; ein leerer
  // String ist kein gültiger Verweis, und SQLite weist die **ganze Zeile** ab
  // (`FOREIGN KEY constraint failed`). Der Schauspieler ist ausdrücklich freiwillig — ohne
  // ihn wäre also **jede** Protokollzeile auf dem Gerät verlorengegangen, und weil die
  // meisten Schreibvorgänge nebenher laufen, lautlos.
  //
  // Am 2026-09-10 auf dem iOS-15-Simulator gemessen: `CONTAINER_START schreiben
  // fehlgeschlagen: … FOREIGN KEY constraint failed`. Der IndexedDB-Adapter des Testlaufs
  // prüft keine Fremdschlüssel und hätte es nie gezeigt.
  const kennung = (wert: string | null | undefined) => (wert ? wert : null);

  const zeile: ExamLogRow = {
    id: uuidv7(),
    actorID: kennung(draft.actorID),
    candidateID: kennung(draft.candidateID),
    examID: kennung(draft.examID),
    examinerID: kennung(draft.examinerID),
    examLogTypeID: logTypeId(draft.type),
    creationDate: draft.creationDate ?? toRelayDateTime(new Date()),
    deviceIdentifier: gerät.deviceIdentifier,
    deviceName: gerät.deviceName,
    pointsNumerator: draft.points ? draft.points.n : null,
    pointsDenominator: draft.points ? draft.points.d : null,
    referenceIdentifier: draft.referenceIdentifier ?? null,
    referenceType: ziel,
    // Dieselbe Uhr wie `creationDate`: auf dem Gerät fallen Bewerten und Speichern zusammen.
    // Die Spalte ist die Ankunftszeit, nicht die Übertragungsmarke — die steht daneben.
    storeDate: draft.creationDate ?? toRelayDateTime(new Date()),
    transferred: 'N',
    value: draft.value ?? '',
  };

  await store.put('ExamLog', [zeile]);
  return zeile;
}

const schlüssel = (z: ExamLogRow) =>
  [z.candidateID, z.examLogTypeID, z.referenceType, z.referenceIdentifier].join('|');

/**
 * Aufsteigend nach `creationDate` sortieren, bei Gleichstand nach `id`. Ein einziger, an einer
 * Stelle definierter Vergleich für beide Lesefunktionen unten — mit gewöhnlichen
 * Vergleichsoperatoren statt `localeCompare`: das feste Hex-/Ziffernformat beider Felder braucht
 * keine gebietsschema-abhängige Sortierung, und zwei Vergleichsarten für dieselbe Ordnung wären
 * nur eine Uneinheitlichkeit ohne Nutzen.
 *
 * Weder IndexedDB (liefert `getAll()` nach Primärschlüssel) noch `sqlite.ts` (`SELECT` ohne
 * `ORDER BY`) liefern Zeilen in Schreibreihenfolge — deshalb wird hier **ausdrücklich** sortiert,
 * statt sich auf die Reihenfolge von `store.all()` zu verlassen. Maßgeblich ist `creationDate`,
 * das Feld, das die Regel nennt — nicht `id`. Bei gleicher `creationDate` (zwei Einträge in
 * derselben Millisekunde) entscheidet `id`: `uuidv7()` führt seinen Zufallsanteil inzwischen als
 * Zähler statt ihn neu zu würfeln (`platform/uuid.ts`), ist also auch innerhalb derselben
 * Millisekunde streng aufsteigend — die Kennung trifft damit tatsächlich den zuletzt
 * geschriebenen Eintrag, nicht nur irgendeinen definierten, aber zufälligen Ausgang.
 */
function nachCreationDateUndIdSortiert(zeilen: ExamLogRow[]): ExamLogRow[] {
  return [...zeilen].sort((a, b) => {
    if (a.creationDate !== b.creationDate) return a.creationDate < b.creationDate ? -1 : 1;
    if (a.id !== b.id) return a.id < b.id ? -1 : 1;
    return 0;
  });
}

/** Der Stand: je Schlüssel der jüngste Eintrag. Beliebig oft wiederholbar, heilt sich selbst. */
export async function latestLogs(store: Store, examId: string, candidateId: string): Promise<ExamLogRow[]> {
  const alle = await store.all<ExamLogRow>('ExamLog', { examID: examId, candidateID: candidateId });
  const jüngste = new Map<string, ExamLogRow>();
  // Aufsteigend einsortiert und je Schlüssel überschrieben: die Map behält am Ende genau den
  // größten (creationDate, id) je Schlüssel — kein `>=`-Vergleich auf unsortierten Daten nötig.
  for (const zeile of nachCreationDateUndIdSortiert(alle)) jüngste.set(schlüssel(zeile), zeile);
  return [...jüngste.values()];
}

/**
 * Die Station, an der dieser Prüfling zuletzt begonnen hat.
 *
 * `Client` merkt sich die Station nicht — Relay führt dort nur Prüfung, Prüfling, Prüfer und
 * Schauspieler. Sie steht aber ohnehin im Protokoll: `kandidatBegonnen` schreibt bei jeder
 * Prüflingswahl ein `CONTAINER_START` mit der Station in `referenceIdentifier`, und zwar
 * **bevor** irgendetwas bewertet werden kann.
 *
 * ⚠️ Nicht aus der Bewertung erschliessen. Der erste Versuch suchte die Station, zu deren
 * Aufgaben die bewerteten gehören — das trifft daneben, sobald zwei Stationen dieselbe
 * Aufgabenkennung führen, wie es die Beispieldaten tun. Das Protokoll weiss es genau.
 */
export async function letzteStation(
  store: Store,
  examId: string,
  candidateId: string,
): Promise<string | null> {
  const alle = await store.all<ExamLogRow>('ExamLog', {
    examID: examId,
    candidateID: candidateId,
    examLogTypeID: logTypeId('CONTAINER_START'),
  });
  const sortiert = nachCreationDateUndIdSortiert(alle);
  return sortiert.length ? (sortiert[sortiert.length - 1].referenceIdentifier ?? null) : null;
}

/**
 * Der Ausgangspuffer — keine eigene Tabelle, sondern eine Abfrage über `transferred`.
 *
 * ⚠️ **Nicht über `storeDate`.** Das war bis 2026-09-11 so und war falsch: `storeDate` ist die
 * Ankunftszeit (`METIS-schema.md:1151`), `NOT NULL`, und sie entscheidet nichts. Siehe
 * `ExamLogRow.transferred`.
 */
export async function pendingLogs(store: Store): Promise<ExamLogRow[]> {
  const offen = await store.all<ExamLogRow>('ExamLog', { transferred: 'N' });
  return nachCreationDateUndIdSortiert(offen);
}

/**
 * Hakt ab, was der Server behalten hat.
 *
 * Kein Zeitstempel: was wann ankam, weiß der Server; hier steht nur, **dass** es ankam. Die
 * Zeile selbst bleibt sonst unberührt — `ExamLog` wird angehängt, nicht geändert, und
 * `transferred` ist das einzige Feld, das nicht zum Protokoll gehört.
 */
export async function markSent(store: Store, ids: string[]): Promise<void> {
  for (const id of ids) {
    const [zeile] = await store.all<ExamLogRow>('ExamLog', { id });
    if (zeile) await store.put('ExamLog', [{ ...zeile, transferred: 'Y' }]);
  }
}
